import React, { useState, useMemo, useEffect } from "react";
import {
  List,
  Datagrid,
  TextField,
  DateField,
  TextInput,
  useRecordContext,
  Button as RAButton,
  useNotify,
  useRefresh,
  useDataProvider,
  FunctionField,
  Show,
  SimpleShowLayout,
  Link,
  ReferenceField,
  ArrayField,
  Labeled,
} from "react-admin";
import { useQueryClient } from "react-query";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button as MUIButton,
  Box,
  Chip,
  Link as MUILink,
  Typography,
  Grid,
  Collapse,
  IconButton,
  TextField as MUITextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DownloadIcon from "@mui/icons-material/Download";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { UserLinkField } from "./users";
import { WorkspaceLinkField } from "./workspaces";

const DataExportBatchExportsList = () => {
  const record = useRecordContext();
  
  if (!record?.batch_exports || record.batch_exports.length === 0) {
    return null;
  }

  return (
    <>
      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
        Batch Exports ({record.batch_exports.length})
      </Typography>
      <ArrayField source="batch_exports">
        <Datagrid
          bulkActionButtons={false}
          rowClick={null}
          sx={{
            width: "100%",
          }}
        >
          <TextField source="id" label="ID" />
          <DateField source="last_updated_at" label="Last Updated At" showTime />
          <FunctionField
            label="Total Records"
            render={(record) => {
                const totals = record?.total_records || record?.total_records_to_export || {};
                return `${totals.total_users || 0} users, ${totals.total_channels || 0} conversations, ${totals.total_messages || 0} messages`;
            }}
          />

            <FunctionField
            label="Completion Percentage"
            render={(record) => {
                const completion = record.completion_percentage || {};
                return (
                <Box>
                    <div><strong>Users:</strong> {completion.users || 0}%</div>
                    <div><strong>Conversations:</strong> {completion.conversations || 0}%</div>
                    <div><strong>Messages:</strong> {completion.messages || 0}%</div>
                </Box>
                );
            }}
          />          
            <FunctionField
                label="Status"
                render={(record) => <StatusChip record={record} />}
            />
          <FunctionField
            label="Download Link"
            render={(record) => <DownloadLinkField record={record} />}
          />
          <DateField source="expires_at" label="Link Expires At" showTime />          
          <FunctionField
            label="Errors"
            render={(record) => {
              const errors = record.export_errors || [];
              return errors.length > 0 ? (
                <Chip label={`${errors.length} error(s)`} color="error" size="small" />
              ) : (
                <Chip label="No errors" color="success" size="small" />
              );
            }}
          />
        </Datagrid>
      </ArrayField>
    </>
  );
};

const ExportConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  isSubmitting,
  exportType,
  workspaceId,
  startDate,
  endDate,
  userIds = [],
  conversationIds = [],
}) => {
  const dataProvider = useDataProvider();
  const [workspaceName, setWorkspaceName] = useState("");
  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  const [workspaceError, setWorkspaceError] = useState("");

  useEffect(() => {
    let isActive = true;

    if (!open || !workspaceId) {
      setWorkspaceName("");
      setWorkspaceLoading(false);
      setWorkspaceError("");
      return;
    }

    setWorkspaceLoading(true);
    setWorkspaceError("");
    dataProvider
      .getOne("workspaces", { id: workspaceId })
      .then(({ data }) => {
        if (!isActive) return;
        setWorkspaceName(data?.name || "");
      })
      .catch((error) => {
        if (!isActive) return;
        setWorkspaceError(
          error?.message || error?.body?.message || "Unable to load workspace name"
        );
        setWorkspaceName("");
      })
      .finally(() => {
        if (!isActive) return;
        setWorkspaceLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [open, workspaceId, dataProvider]);

  const formattedStartDate = startDate
    ? (() => {
        try {
          return new Date(startDate).toLocaleString();
        } catch {
          return startDate;
        }
      })()
    : "--";

  const formattedEndDate = endDate
    ? (() => {
        try {
          return new Date(endDate).toLocaleString();
        } catch {
          return endDate;
        }
      })()
    : "--";

  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Confirm Data Export</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                Workspace
              </Typography>
              <Typography variant="body2">
                {workspaceLoading
                  ? "Loading workspace name..."
                  : workspaceError
                  ? `Error: ${workspaceError}`
                  : workspaceName || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                Export Type
              </Typography>
              <Typography variant="body2">{exportType}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                From Date
              </Typography>
              <Typography variant="body2">{formattedStartDate}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                To Date
              </Typography>
              <Typography variant="body2">{formattedEndDate}</Typography>
            </Grid>
          </Grid>

          {exportType === "Users" && (
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                Users ({userIds.length})
              </Typography>
              {userIds.length === 0 ? (
                <Typography variant="body2">None</Typography>
              ) : (
                <Grid container spacing={1}>
                  {userIds.map((id, index) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      lg={3}
                      key={id || index}
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      <ReferenceField source="id" reference="users" record={{ id }}>
                        <UserLinkField />
                      </ReferenceField>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}

          {exportType === "Conversations" && (
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                Conversations ({conversationIds.length})
              </Typography>
              {conversationIds.length === 0 ? (
                <Typography variant="body2">None</Typography>
              ) : (
                <Grid container spacing={1}>
                  {conversationIds.map((id, index) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      lg={3}
                      key={id || index}
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      <Link
                        to={`/channels/${id}`}
                        style={{
                          fontSize: "12px",
                          wordBreak: "break-all",
                          display: "block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {id}
                      </Link>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <MUIButton onClick={onClose} disabled={isSubmitting}>
          Back
        </MUIButton>
        <MUIButton
          onClick={onConfirm}
          variant="contained"
          color="primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Creating..." : "Confirm"}
        </MUIButton>
      </DialogActions>
    </Dialog>
  );
};

const CreateExportDialog = ({ open, onClose }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [pendingExport, setPendingExport] = useState(null);
  
  // Form state
  const [workspaceId, setWorkspaceId] = useState("");
  const [exportType, setExportType] = useState("Users"); // "Users" or "Conversations"
  const [userIds, setUserIds] = useState("");
  const [conversationIds, setConversationIds] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const formatDateToISO = (value) => {
    if (!value) {
      return undefined;
    }

    // Handle datetime-local without seconds (e.g., "2025-01-01T12:30")
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) {
      const [datePart, timePart] = value.split("T");
      const [year, month, day] = datePart.split("-").map(Number);
      const [hour, minute] = timePart.split(":").map(Number);
      return new Date(year, month - 1, day, hour, minute, 0, 0).toISOString();
    }

    // Handle datetime-local with seconds but no timezone (e.g., "2025-01-01T12:30:45")
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(value)) {
      const [datePart, timePart] = value.split("T");
      const [year, month, day] = datePart.split("-").map(Number);
      const [hour, minute, second] = timePart.split(":").map(Number);
      return new Date(year, month - 1, day, hour, minute, second, 0).toISOString();
    }

    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString();
    }

    return value;
  };

  const parseIdInput = (value) => {
    if (!value) {
      return [];
    }

    const cleaned = value.replace(/['"]/g, "");
    const unique = new Set();

    cleaned.split(",").forEach((raw) => {
      const trimmed = raw.trim();
      if (trimmed.length > 0) {
        unique.add(trimmed);
      }
    });

    return Array.from(unique);
  };

  // Calculate number of users/conversations for validation
  const MAX_USERS = 100;
  const MAX_CONVERSATIONS = 500;

  const parsedUserIds = useMemo(() => parseIdInput(userIds), [userIds]);
  const userCount = parsedUserIds.length;
  const exceedsUserLimit = userCount > MAX_USERS;

  const parsedConversationIds = useMemo(
    () => parseIdInput(conversationIds),
    [conversationIds]
  );
  const conversationCount = parsedConversationIds.length;
  const exceedsConversationLimit = conversationCount > MAX_CONVERSATIONS;

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setWorkspaceId("");
      setExportType("Users");
      setUserIds("");
      setConversationIds("");
      setStartDate("");
      setEndDate("");
      setPendingExport(null);
      setConfirmationOpen(false);
    }
  }, [open]);

  const handlePrepareExport = () => {
    if (!workspaceId || !workspaceId.trim()) {
      notify("Please enter a workspace ID", "error");
      return;
    }

    if (!startDate || !startDate.trim()) {
      notify("Please select a start date", "error");
      return;
    }

    if (!endDate || !endDate.trim()) {
      notify("Please select an end date", "error");
      return;
    }

    if (exportType === "Users") {
      if (!userIds || !userIds.trim()) {
        notify("Please enter at least one user ID", "error");
        return;
      }

      // Parse user_ids from comma-separated string
      const dedupedUserIds = parseIdInput(userIds);

      if (dedupedUserIds.length === 0) {
        notify("Please enter at least one valid user ID", "error");
        return;
      }

      // Validate maximum number of users (100 limit)
      if (dedupedUserIds.length > MAX_USERS) {
        notify(
          `Maximum ${MAX_USERS} users allowed for export. You provided ${dedupedUserIds.length} users.`,
          "error"
        );
        return;
      }

      const data = {
        workspace_id: workspaceId.trim(),
        user_ids: dedupedUserIds, // Always an array
        start_date: formatDateToISO(startDate),
        end_date: formatDateToISO(endDate),
      };

      Object.keys(data).forEach((key) => {
        if (data[key] === undefined) {
          delete data[key];
        }
      });

      setPendingExport({
        exportType: "Users",
        data,
        userIds: dedupedUserIds,
        conversationIds: [],
        workspaceId: workspaceId.trim(),
        startDate: data.start_date,
        endDate: data.end_date,
      });
      setConfirmationOpen(true);
    } else if (exportType === "Conversations") {
      if (!conversationIds || !conversationIds.trim()) {
        notify("Please enter at least one conversation ID", "error");
        return;
      }

      // Parse conversation_ids from comma-separated string
      const dedupedConversationIds = parseIdInput(conversationIds);

      if (dedupedConversationIds.length === 0) {
        notify("Please enter at least one valid conversation ID", "error");
        return;
      }

      // Validate maximum number of conversations (500 limit)
      if (dedupedConversationIds.length > MAX_CONVERSATIONS) {
        notify(
          `Maximum ${MAX_CONVERSATIONS} conversations allowed for export. You provided ${dedupedConversationIds.length} conversations.`,
          "error"
        );
        return;
      }

      const data = {
        workspace_id: workspaceId.trim(),
        conversation_ids: dedupedConversationIds, // Always an array
        start_date: formatDateToISO(startDate),
        end_date: formatDateToISO(endDate),
      };

      Object.keys(data).forEach((key) => {
        if (data[key] === undefined) {
          delete data[key];
        }
      });

      setPendingExport({
        exportType: "Conversations",
        data,
        userIds: [],
        conversationIds: dedupedConversationIds,
        workspaceId: workspaceId.trim(),
        startDate: data.start_date,
        endDate: data.end_date,
      });
      setConfirmationOpen(true);
    }
  };

  const handleConfirmExport = async () => {
    if (!pendingExport) {
      return;
    }

    setIsLoading(true);
    try {
      console.log("Creating data export with payload:", pendingExport.data);
      await dataProvider.create("data-exporter", {
        data: pendingExport.data,
        exportType: pendingExport.exportType,
      });
      notify("Data export created successfully", "success");
      setConfirmationOpen(false);
      setPendingExport(null);
      onClose();
      queryClient.invalidateQueries(["data-exporter", "getList"]);
      refresh();
    } catch (error) {
      console.error("Failed to create data export:", error);
      let errorMessage = "Failed to create data export";

      if (error?.body) {
        if (typeof error.body === "string") {
          try {
            const parsed = JSON.parse(error.body);
            errorMessage =
              parsed.message ||
              parsed.error ||
              parsed.errmsg ||
              parsed.detail ||
              errorMessage;
          } catch {
            errorMessage = error.body.trim() || errorMessage;
          }
        } else if (typeof error.body === "object") {
          errorMessage =
            error.body.message ||
            error.body.error ||
            error.body.errmsg ||
            error.body.detail ||
            errorMessage;
        }
      } else if (error?.json) {
        if (typeof error.json === "object") {
          errorMessage =
            error.json.message ||
            error.json.error ||
            error.json.errmsg ||
            error.json.detail ||
            errorMessage;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (error?.statusText) {
        errorMessage = `${error.statusText}${error.status ? ` (${error.status})` : ""}`;
      }

      notify(errorMessage, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Create New Data Export</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Workspace ID Input */}
          <MUITextField
            label="Workspace ID"
            value={workspaceId}
            onChange={(e) => setWorkspaceId(e.target.value)}
            fullWidth
            required
            disabled={isLoading}
            placeholder="Enter workspace ID"
            error={!workspaceId || !workspaceId.trim()}
            helperText={!workspaceId || !workspaceId.trim() ? "Workspace ID is required" : ""}
          />

          {/* Export Type Select */}
          <FormControl fullWidth required>
            <InputLabel>Type</InputLabel>
            <Select
              value={exportType}
              onChange={(e) => {
                setExportType(e.target.value);
                // Clear the other field when switching types
                if (e.target.value === "Users") {
                  setConversationIds("");
                } else {
                  setUserIds("");
                }
              }}
              label="Type"
              disabled={isLoading}
            >
              <MenuItem value="Users">Users</MenuItem>
              <MenuItem value="Conversations">Conversations</MenuItem>
            </Select>
          </FormControl>

          {/* User IDs Input - shown only when type is Users */}
          {exportType === "Users" && (
            <MUITextField
              label="User IDs"
              value={userIds}
              onChange={(e) => setUserIds(e.target.value)}
              fullWidth
              required
              disabled={isLoading}
              placeholder="user1, user2, user3"
              helperText={
                !userIds || !userIds.trim() 
                  ? "User IDs are required (comma-separated)" 
                  : exceedsUserLimit
                  ? `Error: Maximum ${MAX_USERS} users allowed. You have entered ${userCount} users.`
                  : userCount > 0
                  ? `Enter user IDs separated by commas (e.g., user1, user2, user3). Maximum ${MAX_USERS} users allowed. (${userCount} user${userCount !== 1 ? 's' : ''} entered)`
                  : `Enter user IDs separated by commas (e.g., user1, user2, user3). Maximum ${MAX_USERS} users allowed.`
              }
              multiline
              rows={3}
              error={!userIds || !userIds.trim() || exceedsUserLimit}
            />
          )}

          {/* Conversation IDs Input - shown only when type is Conversations */}
          {exportType === "Conversations" && (
            <MUITextField
              label="Conversation IDs"
              value={conversationIds}
              onChange={(e) => setConversationIds(e.target.value)}
              fullWidth
              required
              disabled={isLoading}
              placeholder="conv1, conv2, conv3"
              helperText={
                !conversationIds || !conversationIds.trim() 
                  ? "Conversation IDs are required (comma-separated)" 
                  : exceedsConversationLimit
                  ? `Error: Maximum ${MAX_CONVERSATIONS} conversations allowed. You have entered ${conversationCount} conversations.`
                  : conversationCount > 0
                  ? `Enter conversation IDs separated by commas (e.g., conv1, conv2, conv3). Maximum ${MAX_CONVERSATIONS} conversations allowed. (${conversationCount} conversation${conversationCount !== 1 ? 's' : ''} entered)`
                  : `Enter conversation IDs separated by commas (e.g., conv1, conv2, conv3). Maximum ${MAX_CONVERSATIONS} conversations allowed.`
              }
              multiline
              rows={3}
              error={!conversationIds || !conversationIds.trim() || exceedsConversationLimit}
            />
          )}

          {/* Start Date */}
          <MUITextField
            label="From Date"
            type="datetime-local"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            fullWidth
            InputLabelProps={{
              shrink: true,
            }}
            disabled={isLoading}
            required
            error={!startDate || !startDate.trim()}
            helperText={!startDate || !startDate.trim() ? "Start date is required" : ""}
          />

          {/* End Date */}
          <MUITextField
            label="To Date"
            type="datetime-local"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            fullWidth
            InputLabelProps={{
              shrink: true,
            }}
            disabled={isLoading}
            required
            error={!endDate || !endDate.trim()}
            helperText={!endDate || !endDate.trim() ? "End date is required" : ""}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <MUIButton onClick={onClose} disabled={isLoading}>
          Cancel
        </MUIButton>
        <MUIButton
          onClick={handlePrepareExport}
          variant="contained"
          color="primary"
                  disabled={
                    isLoading || 
                    !workspaceId || 
                    !workspaceId.trim() || 
                    !startDate ||
                    !startDate.trim() ||
                    !endDate ||
                    !endDate.trim() ||
                    (exportType === "Users" && (!userIds || !userIds.trim() || exceedsUserLimit)) ||
                    (exportType === "Conversations" && (!conversationIds || !conversationIds.trim() || exceedsConversationLimit))
                  }
        >
          Create Export
        </MUIButton>
      </DialogActions>
    </Dialog>
      <ExportConfirmationDialog
        open={confirmationOpen}
        onClose={() => {
          if (isLoading) return;
          setConfirmationOpen(false);
        }}
        onConfirm={handleConfirmExport}
        isSubmitting={isLoading}
        exportType={pendingExport?.exportType || exportType}
        workspaceId={pendingExport?.workspaceId || workspaceId.trim()}
        startDate={pendingExport?.startDate}
        endDate={pendingExport?.endDate}
        userIds={pendingExport?.userIds || []}
        conversationIds={pendingExport?.conversationIds || []}
      />
    </>
  );
};

const CreateExportButton = () => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <RAButton
        label="Create Export"
        onClick={() => setOpen(true)}
        startIcon={<CloudUploadIcon />}
      />
      <CreateExportDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
};

const StatusChip = ({ record }) => {
  if (!record) {
    return <Chip label="unknown" color="default" size="small" />;
  }
  
  const status = record.status ? String(record.status).toLowerCase().trim() : "unknown";
  
  // Check if this batch export has errors
  const errors = record.export_errors || [];
  const hasErrors =
    typeof record.has_errors === "boolean"
      ? record.has_errors
      : Array.isArray(errors) && errors.length > 0;
  
  // Determine label and color
  let label = status;
  let color = "default";
  
  switch (status) {
      case "completed":
      case "ready":
      if (hasErrors) {
        label = "completed with errors";
        color = "warning"; // Use warning (orange) for completed with errors
      } else {
        color = "success";
      }
      break;
      case "processing":
      case "in_progress":
      color = "primary"; // Use primary (blue) for in progress
      break;
      case "failed":
      case "error":
      color = "error";
      break;
      default:
      color = "default";
    }

  return <Chip label={label} color={color} size="small" />;
};

const DownloadLinkField = ({ record }) => {
  if (!record.download_url) {
    return <span>-</span>;
  }

  // Check if the link has expired
  if (record.expires_at) {
    const expiresAt = new Date(record.expires_at);
    const now = new Date();
    if (expiresAt < now) {
      return <Chip label="Link Expired" color="error" size="small" />;
    }
  }

  return (
    <MUILink
      href={record.download_url}
      target="_blank"
      rel="noopener noreferrer"
      underline="hover"
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <DownloadIcon fontSize="small" />
        Download
      </Box>
    </MUILink>
  );
};

const dataExportFilters = [
  <TextInput label="Batch ID" source="batch_id" alwaysOn />,
  <TextInput label="Creator ID" source="creator_id" alwaysOn />,
  <TextInput label="Status" source="status" alwaysOn />,
  <TextInput label="Workspace ID" source="workspace_id" alwaysOn />,
];

const TotalRecordsField = ({ record }) => {
  const totals = record?.total_records || record?.total_records_to_export;

  if (!totals) {
    return <span>-</span>;
  }

  const {
    total_users = 0,
    total_channels = 0,
    total_messages = 0,
  } = totals;

  return (
    <span>
      Users: {total_users}, Conversations: {total_channels}, Messages: {total_messages}
    </span>
  );
};

const CollapsibleConversationIds = () => {
  const record = useRecordContext();
  const [expanded, setExpanded] = useState(false);
  
  // Collect all conversation_ids from all batch_exports and flatten
  // Since there are no duplicates per batch_export, we can skip Set conversion for better performance
  const allConvIds = useMemo(() => {
    if (!record?.batch_exports || record.batch_exports.length === 0) {
      return [];
    }
    
    // Flatten all conversation_ids from all batch_exports
    // Using flatMap is efficient and creates a single pass through the data
    return record.batch_exports.flatMap(batchExport => 
      batchExport.conversation_ids || []
    );
  }, [record?.batch_exports]);
  
  // Use root-level total_channels from the API response, or fallback to flattened count
  const totalChannels = record?.total_channels || allConvIds.length;

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  if (allConvIds.length === 0) {
    return (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
          Conversation IDs ({totalChannels})
        </Typography>
        None
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          mb: 1,
        }}
        onClick={toggleExpanded}
      >
        <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 1 }}>
          Conversation IDs ({totalChannels})
        </Typography>
        <IconButton size="small" onClick={toggleExpanded}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Grid container spacing={1}>
          {allConvIds.map((id, index) => (
            <Grid 
              item 
              xs={12} 
              sm={6} 
              md={4} 
              lg={1} 
              xl={1}
              key={id || index}
              sx={{
                width: { xs: '100%', sm: '50%', md: '33.333%', lg: '12.5%', xl: '12.5%' },
                flexBasis: { lg: '12.5%', xl: '12.5%' },
                maxWidth: { lg: '12.5%', xl: '12.5%' },
                flexGrow: 0,
                marginBottom: '5px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              <Link 
                to={`/channels/${id}`}
                style={{
                  fontSize: '12px',
                  wordBreak: 'break-all',
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {id}
              </Link>
            </Grid>
          ))}
        </Grid>
      </Collapse>
    </Box>
  );
};

const UsersFieldWithCount = () => {
  const record = useRecordContext();
  
  // Collect all user_ids from all batch_exports and flatten/deduplicate
  const allUserIds = useMemo(() => {
    if (!record?.batch_exports || record.batch_exports.length === 0) {
      return [];
    }
    
    // Flatten all user_ids from all batch_exports
    const flattened = record.batch_exports.flatMap(batchExport => 
      batchExport.user_ids || []
    );
    
    // Remove duplicates by converting to Set and back to array
    return [...new Set(flattened)];
  }, [record?.batch_exports]);
  
  // Use root-level total_users from the API response, or fallback to unique count
  const totalUsers = record?.total_users || allUserIds.length;
  
  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
        Users ({totalUsers})
      </Typography>
      {allUserIds.length > 0 ? (
            <Grid container spacing={1}>
          {allUserIds.map((id, index) => (
                <Grid 
                  item 
                  xs={12} 
                  sm={6} 
                  md={4} 
                  lg={1} 
                  xl={1}
              key={id || index}
                  sx={{
                    width: { xs: '100%', sm: '50%', md: '33.333%', lg: '12.5%', xl: '12.5%' },
                    flexBasis: { lg: '12.5%', xl: '12.5%' },
                    maxWidth: { lg: '12.5%', xl: '12.5%' },
                    flexGrow: 0,
                    marginBottom: '5px',
                  }}
                >
                  <ReferenceField source="id" reference="users" record={{ id }}>
                    <UserLinkField />
                  </ReferenceField>
                </Grid>
              ))}
            </Grid>
          ) : (
            "None"
      )}
    </Box>
  );
};

const CompletionStatusField = ({ record }) => {
  if (!record) {
    return <span>-</span>;
  }
  
  // Status is already aggregated from all exports in the batch
  const status = record.status ? String(record.status).toLowerCase().trim() : 'unknown';
  
  // Check if there are any errors in any batch_exports or root-level export_errors
  // Check root-level export_errors first (aggregated errors)
  const rootErrors = record.export_errors;
  const hasRootErrors = rootErrors && Array.isArray(rootErrors) && rootErrors.length > 0;
  
  // Check batch_exports for errors
  const batchExports = record.batch_exports;
  const hasBatchErrors = batchExports && Array.isArray(batchExports) && batchExports.length > 0
    ? batchExports.some(batchExport => {
        const errors = batchExport?.export_errors || [];
        return Array.isArray(errors) && errors.length > 0;
      })
    : false;
  
  const hasErrors =
    typeof record.has_errors === "boolean"
      ? record.has_errors
      : hasRootErrors || hasBatchErrors;
  
  switch (status) {
    case 'failed':
      return <Chip label="Failed" color="error" size="small" />;
    case 'partially_failed':
      return <Chip label="Partially Failed" color="error" size="small" />;
    case 'compressing_and_uploading':
      return <Chip label="Compressing & Uploading" color="info" size="small" />;
    case 'in_progress':
      return <Chip label="In Progress" color="primary" size="small" />;
    case 'completed':
      // If completed but has errors, show "Completed with errors" in warning color (orange)
      if (hasErrors) {
        return <Chip label="Completed with errors" color="warning" size="small" />;
      }
      return <Chip label="Completed" color="success" size="small" />;
    case 'created':
      return <Chip label="Created" color="default" size="small" />;
    default:
      // Fallback to completion_status if status is unknown
      if (!record?.completion_status) {
        return <span>-</span>;
      }
      const { users = false, conversations = false, messages = false } = record.completion_status;
      const allComplete = users && conversations && messages;
      const someComplete = users || conversations || messages;
      
      if (allComplete) {
        // If complete but has errors, show "Complete with errors" in warning color (orange)
        if (hasErrors) {
          return <Chip label="Complete with errors" color="warning" size="small" />;
        }
        return <Chip label="Complete" color="success" size="small" />;
      } else if (someComplete) {
        return <Chip label="In Progress" color="primary" size="small" />;
      } else {
        return <Chip label="Not Started" color="default" size="small" />;
      }
  }
};

const CollapsibleExportErrors = () => {
  const record = useRecordContext();
  const [expanded, setExpanded] = useState(false);
  const MAX_DISPLAYED_ERRORS = 100;
  const MAX_TOTAL_ERRORS = 500;

  const { aggregatedErrors, displayedErrors, hasMoreErrors, totalErrors, isTruncated } = useMemo(() => {
    if (!record) {
      return {
        aggregatedErrors: [],
        displayedErrors: [],
        hasMoreErrors: false,
        totalErrors: 0,
        isTruncated: false,
      };
    }

    const rootErrors = Array.isArray(record.export_errors) ? record.export_errors : [];
    const batchErrors = Array.isArray(record.batch_exports)
      ? record.batch_exports.flatMap((batch) =>
          Array.isArray(batch.export_errors) ? batch.export_errors : []
        )
      : [];

    const mergedErrors = [...rootErrors, ...batchErrors];
    const dedupedErrors = mergedErrors.reduce((acc, error) => {
      if (!acc.seen.has(error)) {
        acc.seen.add(error);
        acc.items.push(error);
      }
      return acc;
    }, { seen: new Set(), items: [] }).items;

    const total = dedupedErrors.length;
    const limitedToStored = total > MAX_TOTAL_ERRORS ? dedupedErrors.slice(-MAX_TOTAL_ERRORS) : dedupedErrors;
    const limitedToDisplay =
      limitedToStored.length > MAX_DISPLAYED_ERRORS
        ? limitedToStored.slice(-MAX_DISPLAYED_ERRORS)
        : limitedToStored;

    return {
      aggregatedErrors: limitedToStored,
      displayedErrors: limitedToDisplay,
      hasMoreErrors: limitedToStored.length > MAX_DISPLAYED_ERRORS,
      totalErrors: total,
      isTruncated: total > MAX_TOTAL_ERRORS,
    };
  }, [record]);

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  if (aggregatedErrors.length === 0) {
    return (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
          Export Errors
        </Typography>
        No errors
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          mb: 1,
        }}
        onClick={toggleExpanded}
      >
        <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 1 }}>
          Export Errors ({totalErrors}{isTruncated ? '+' : ''} {isTruncated ? `(max ${MAX_TOTAL_ERRORS} stored)` : ''})
        </Typography>
        <IconButton size="small" onClick={toggleExpanded}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Box>
          {hasMoreErrors && (
            <Box
              sx={{
                p: 1.5,
                mb: 2,
                backgroundColor: "#fff3cd",
                border: "1px solid #ffc107",
                borderRadius: 1,
                fontSize: "0.9em",
              }}
            >
              <strong>Note:</strong> Showing last {Math.min(MAX_DISPLAYED_ERRORS, aggregatedErrors.length)} of {totalErrors} error{totalErrors !== 1 ? 's' : ''}.
              {isTruncated && ` Only the most recent ${MAX_TOTAL_ERRORS} errors are stored.`}
            </Box>
          )}
          {displayedErrors.map((error, index) => (
            <Box
              key={index}
              sx={{
                p: 1,
                mb: 1,
                backgroundColor: "#fee",
                border: "1px solid #fcc",
                borderRadius: 1,
                fontFamily: "monospace",
                fontSize: "0.85em",
                whiteSpace: "pre-wrap",
              }}
            >
              {error}
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export const DataExportList = (props) => (
  <List
    {...props}
    filters={dataExportFilters}
    actions={<CreateExportButton />}
    sort={{ field: "created_at", order: "DESC" }}
  >
    <Datagrid rowClick="show">
      <TextField source="batch_id" label="Batch ID" />
      <ReferenceField source="creator_id" reference="users" label="Creator">
        <UserLinkField />
      </ReferenceField>
      <ReferenceField source="workspace_id" reference="workspaces" label="Workspace">
        <WorkspaceLinkField />
      </ReferenceField>
      <DateField source="created_at" label="Created At" showTime />
      <FunctionField
        label="From Date"
        render={(record) => {
          if (!record?.start_date) {
            return "--";
          }
          try {
            return new Date(record.start_date).toLocaleString();
          } catch (e) {
            return record.start_date;
          }
        }}
      />
      <FunctionField
        label="To Date"
        render={(record) => {
          if (!record?.end_date) {
            return "--";
          }
          try {
            return new Date(record.end_date).toLocaleString();
          } catch (e) {
            return record.end_date;
          }
        }}
      />
      <DateField source="expires_at" label="Expires At" showTime />
      <FunctionField
        label="Total Records"
        render={(record) => <TotalRecordsField record={record} />}
      />
      <FunctionField
        label="Status"
        render={(record) => <CompletionStatusField record={record} />}
      />
    </Datagrid>
  </List>
);

export const DataExportShow = (props) => {
  return (
    <Show {...props} title={(record) => `Data Export ${record?.batch_id || record?.id || ''}`}>
      <SimpleShowLayout>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Labeled label="Batch ID">
              <TextField source="batch_id" />
            </Labeled>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Labeled label="Creator">
              <ReferenceField source="creator_id" reference="users" label="Creator">
                <UserLinkField />
              </ReferenceField>
            </Labeled>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Labeled label="Workspace">
              <ReferenceField source="workspace_id" reference="workspaces" label="Workspace">
                <WorkspaceLinkField />
              </ReferenceField>
            </Labeled>
          </Grid>



          
        <Grid item xs={12} sm={6} md={4}>
          <Labeled label="Status">
          <FunctionField
          label="Status"
          render={(record) => <CompletionStatusField record={record} />}
        />
          </Labeled>
        </Grid>

          
          <Grid item xs={12} sm={6} md={4}>
            <Labeled label="Created At">
              <DateField source="created_at" showTime />
            </Labeled>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Labeled label="Last Updated At">
              <DateField source="last_updated_at" showTime />
            </Labeled>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Labeled label="Expires At">
              <DateField source="expires_at" showTime />
            </Labeled>
          </Grid>
        </Grid>

          <Grid item xs={6} sm={4} md={4}>
            <Box sx={{ backgroundColor: "#f5f5f5", p: 1.5, borderRadius: 1, border: "1px solid #e0e0e0" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "20px", mb: 1.5 }}>
                Summary
              </Typography>
              
              <Grid container spacing={1}>
                <Grid item xs={2} sm={2} md={2}>
                  <Labeled label="From Date">
                    <FunctionField
                      render={(record) => {
                        if (!record.start_date) return "--";
                        const date = new Date(record.start_date);
                        return date.toLocaleString();
                      }}
                    />
                  </Labeled>
                </Grid>
                <Grid item xs={4} sm={8} md={8}>
                  <Labeled label="To Date">
                    <FunctionField
                      render={(record) => {
                        if (!record.end_date) return "--";
                        const date = new Date(record.end_date);
                        return date.toLocaleString();
                      }}
                    />
                  </Labeled>
                </Grid>
                <Grid item xs={3} sm={3} md={3}>
                  <Labeled label="Total Exports">
                    <TextField source="total_batches" />
                  </Labeled>
                </Grid>
                <Grid item xs={3} sm={3} md={3}>
                  <Labeled label="Total Users">
                    <TextField source="total_users" />
                  </Labeled>
                </Grid>
                <Grid item xs={3} sm={3} md={3}>
                  <Labeled label="Total Conversations">
                    <TextField source="total_channels" />
                  </Labeled>
                </Grid>
                <Grid item xs={3} sm={3} md={3}>
                  <Labeled label="Total Messages">
                    <TextField source="total_messages" />
                  </Labeled>
                </Grid>
              </Grid>
            </Box>
        </Grid>
        
        <DataExportBatchExportsList />

        <UsersFieldWithCount />

        <CollapsibleConversationIds />

        <CollapsibleExportErrors />
      </SimpleShowLayout>
    </Show>
  );
};

